import {
  CARE_PLAN_CASE_STATUS_LABEL,
  CARE_PLAN_UNLINKED_GROUP_KEY,
  CARE_PLAN_UNLINKED_GROUP_LABEL,
  CASE_STATUS_GROUP_ORDER,
  CarePlanCase,
  CarePlanGoal,
} from '../care-plan.model';
import { CarePlanDomain } from '../care-plan-fixture-taxonomy.token';

export type CarePlanGroupBy = 'status' | 'domain' | 'goal';

export interface CarePlanCaseGroup {
  key: string;
  label: string;
  cases: CarePlanCase[];
}

export interface CarePlanGroupContext {
  statusLabel?(status: string): string;
  domains: readonly CarePlanDomain[];
  goals: readonly CarePlanGoal[];
  includeHistoricalGoals?: boolean;
}

/** D20 — Group by renders non-empty groups only. */
export function groupCases(
  cases: readonly CarePlanCase[],
  groupBy: CarePlanGroupBy,
  ctx: CarePlanGroupContext,
): CarePlanCaseGroup[] {
  switch (groupBy) {
    case 'status':
      return groupCasesByStatus(cases, ctx.statusLabel);
    case 'domain':
      return groupCasesByDomain(cases, ctx.domains);
    case 'goal':
      return groupCasesByGoal(cases, ctx.goals, ctx.includeHistoricalGoals);
  }
}

/** Fixed order: In Progress, Monitor, Planned (PRD §4 — not alphabetical, not declaration order). */
export function groupCasesByStatus(
  cases: readonly CarePlanCase[],
  statusLabel: (status: string) => string = (status) =>
    CARE_PLAN_CASE_STATUS_LABEL[status] ?? status,
): CarePlanCaseGroup[] {
  const order = [
    ...new Set([
      ...CASE_STATUS_GROUP_ORDER,
      ...cases.map((item) => item.status),
    ]),
  ];
  return order
    .map((status) => ({
      key: status,
      label: statusLabel(status),
      cases: cases.filter((c) => c.status === status),
    }))
    .filter((group) => group.cases.length > 0);
}

/** Catalogue order for defined domains (D5); omits empty groups (D20). */
export function groupCasesByDomain(
  cases: readonly CarePlanCase[],
  domains: readonly CarePlanDomain[],
): CarePlanCaseGroup[] {
  const known = [...domains]
    .sort((a, b) => a.order - b.order)
    .map((domain) => ({
      key: domain.key,
      label: domain.label,
      cases: cases.filter((c) => c.domain === domain.key),
    }))
    .filter((group) => group.cases.length > 0);
  const knownKeys = new Set(domains.map((domain) => domain.key));
  const unknown = new Map<string, CarePlanCase[]>();
  for (const item of cases) {
    if (knownKeys.has(item.domain)) continue;
    unknown.set(item.domain, [...(unknown.get(item.domain) ?? []), item]);
  }
  return [
    ...known,
    ...[...unknown.entries()].map(([key, grouped]) => ({
      key,
      label: key.startsWith('domain-id:')
        ? `Domain ID ${key.slice('domain-id:'.length)}`
        : key,
      cases: grouped,
    })),
  ];
}

/**
 * Goals in local sort order; live consumers can include historical associations.
 * Goals without matching cases are omitted. The "Not tied to a client goal" bucket sorts last and only
 * renders when non-empty.
 */
export function groupCasesByGoal(
  cases: readonly CarePlanCase[],
  goals: readonly CarePlanGoal[],
  includeHistory = false,
): CarePlanCaseGroup[] {
  const activeGoals = [...goals]
    .filter((g) => includeHistory || g.status === 'active')
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const activeGoalIds = new Set(activeGoals.map((g) => g.id));

  const groups: CarePlanCaseGroup[] = activeGoals
    .map((goal) => ({
      key: goal.id,
      label: goal.displayText,
      cases: cases.filter((c) => c.goalIds.includes(goal.id)),
    }))
    .filter((group) => group.cases.length > 0);

  const orphanCases = cases.filter(
    (c) => !c.goalIds.some((goalId) => activeGoalIds.has(goalId)),
  );
  if (orphanCases.length > 0) {
    groups.push({
      key: CARE_PLAN_UNLINKED_GROUP_KEY,
      label: CARE_PLAN_UNLINKED_GROUP_LABEL,
      cases: orphanCases,
    });
  }
  return groups;
}
